// Create type definitions for modules without declaration files
// Add this at the top of your file

// Import required modules
import Compressor from "compressorjs";
import nftABI from "../contractData/abis/nft.json";
import propertyFactoryABI from "../contractData/abis/propertyFactory.json";
import propertyAsset from "../contractData/abis/propertyAsset.json";
import rentalPoolABI from "../contractData/abis/rentalPool.json";
import escrowABI from "../contractData/abis/escrow.json";
import tokenABI from "../contractData/abis/token.json";
import resellABI from "../contractData/abis/resell.json";
import resellPoolABI from "../contractData/abis/resellpool.json";
import verification from "../contractData/abis/verification.json";
import getContractAddresses from "../contractData/contractAddress/addresses";
import { web3 } from "../web3";
import { getFromMinIO } from "../services/minio.service";
import { Contract } from "web3-eth-contract";
import { AbiItem } from "web3-utils";

// Define types for the contract addresses returned by getContractAddresses
interface ContractAddresses {
  nftContractAddress: string;
  escrowContractAddres: string;
  token: string;
  resellContract: string;
  identityVerification: string;
  propertyFactory: string;
  rentalPool: string;
}

/**
 * Extracts file type from format string (e.g., "jpeg/image" -> "image")
 * @param format - The format string from database
 * @returns The file type for UI rendering
 */
export function extractFileType(format: string | null): string {
  if (!format) return "image";
  // MIME type like "image/jpeg" or "video/mp4" — use the top-level type
  if (format.includes("/")) {
    return format.split("/")[0].toLowerCase();
  }
  // Bare extension — map common ones to their category
  const f = format.toLowerCase();
  const imageFormats = ["jpeg", "jpg", "png", "gif", "webp", "svg"];
  const videoFormats = ["mp4", "mov", "webm", "mkv", "avi"];
  const audioFormats = ["mp3", "wav", "ogg", "m4a", "flac"];
  if (imageFormats.includes(f)) return "image";
  if (videoFormats.includes(f)) return "video";
  if (audioFormats.includes(f)) return "audio";
  return f;
}

/**
 * Compresses an image using Compressor.js
 * @param image - The image file to compress
 * @returns A promise that resolves to the compressed image
 */
export async function compressImage(image: File): Promise<File> {
  return new Promise<File>((resolve, reject) => {
    try {
      new Compressor(image, {
        quality: 0.6, // 0.6 can also be used, but its not recommended to go below.
        success: (compressedResult: File) => {
          resolve(compressedResult);
          // compressedResult has the compressed file.
          // Use the compressed file to upload the images to your server.
        },
      });
    } catch (error) {
      reject(undefined);
    }
  });
}

/**
 * Returns a user-friendly message for transaction errors (e.g. MetaMask "user denied").
 * Use in catch blocks and .on("error") to show a proper message instead of throwing.
 */
export function getTransactionErrorMessage(
  error: unknown,
  defaultMessage: string = "Transaction failed"
): { message: string; isUserDenied: boolean } {
  if (!error || typeof error !== "object") {
    return { message: defaultMessage, isUserDenied: false };
  }
  const err = error as { message?: string; code?: number; reason?: string };
  const msg = String(err.message || err.reason || "").toLowerCase();
  if (
    Number(err.code) === 4001 ||
    msg.includes("user denied") ||
    msg.includes("user rejected") ||
    msg.includes("denied transaction") ||
    msg.includes("rejected the request")
  ) {
    return {
      message:
        "Transaction cancelled. You rejected the request in your wallet.",
      isUserDenied: true,
    };
  }
  return { message: defaultMessage, isUserDenied: false };
}

type ContractSendCallbacks = {
  onTransactionHash?: (hash: string) => void;
  onReceipt?: (receipt: unknown) => void;
};

interface ContractPromiEvent {
  once(
    event: string,
    handler: (...args: unknown[]) => void
  ): ContractPromiEvent;
}

/**
 * Wraps a Web3 contract .send() PromiEvent so callers can await a real
 * receipt (resolve) or wallet rejection / revert (reject).
 */
export function executeContractSend(
  tx: { send: (options: { from: string }) => unknown },
  from: string,
  callbacks?: ContractSendCallbacks
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const promiEvent = tx.send({ from }) as ContractPromiEvent;

    promiEvent.once("transactionHash", (hash: unknown) => {
      if (typeof hash === "string") {
        callbacks?.onTransactionHash?.(hash);
      }
    });
    promiEvent.once("receipt", (receipt: unknown) => {
      callbacks?.onReceipt?.(receipt);
      resolve(receipt);
    });
    promiEvent.once("error", (error: unknown) => {
      reject(error);
    });
  });
}

/**
 * Capitalizes the first letter of a string
 * @param string - The input string
 * @returns The string with the first letter capitalized
 */
export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * Formats large numbers in million/trillion format (e.g., 3,000,000 -> 3M, 1,000,000,000,000 -> 1T)
 * @param num - The number to format
 * @param options - Formatting options
 * @returns Formatted string (e.g., "3M", "1.5B", "2T")
 */
export function formatLargeNumber(
  num: number | string | null | undefined,
  options: {
    includePrefix?: boolean; // Include $ prefix for currency
    decimals?: number; // Number of decimal places (default: 0 for whole numbers, 1 for decimals)
  } = {}
): string {
  if (num === null || num === undefined || num === "") return "0";

  const number = typeof num === "string" ? parseFloat(num) : num;
  if (isNaN(number) || number === 0) return "0";

  const { includePrefix = false, decimals = 0 } = options;
  const prefix = includePrefix ? "$" : "";
  const absNum = Math.abs(number);
  const sign = number < 0 ? "-" : "";

  // Trillion (1,000,000,000,000)
  if (absNum >= 1_000_000_000_000) {
    const value = absNum / 1_000_000_000_000;
    return `${sign}${prefix}${
      decimals > 0 ? value.toFixed(decimals) : Math.round(value)
    }T`;
  }

  // Billion (1,000,000,000)
  if (absNum >= 1_000_000_000) {
    const value = absNum / 1_000_000_000;
    return `${sign}${prefix}${
      decimals > 0 ? value.toFixed(decimals) : Math.round(value)
    }B`;
  }

  // Million (1,000,000)
  if (absNum >= 1_000_000) {
    const value = absNum / 1_000_000;
    return `${sign}${prefix}${
      decimals > 0 ? value.toFixed(decimals) : Math.round(value)
    }M`;
  }

  // Thousand (1,000) - optional, only if number is large enough
  if (absNum >= 1_000) {
    const value = absNum / 1_000;
    return `${sign}${prefix}${
      decimals > 0 ? value.toFixed(decimals) : Math.round(value)
    }K`;
  }

  // For numbers less than 1000, return as is with prefix if needed
  return `${sign}${prefix}${absNum.toLocaleString()}`;
}

/**
 * Gets a contract instance based on the provided parameters
 * @param isEscrow - Whether to get the escrow contract
 * @param isToken - Whether to get the token contract
 * @param isResell - Whether to get the resell contract
 * @param isVerified - Whether to get the verification contract
 * @returns The contract instance or undefined
 */
export function getContractInstance(
  isEscrow?: boolean,
  isToken?: string,
  isResell?: boolean,
  isResellPool?: boolean,
  isVerified?: boolean
): Contract | undefined {
  const {
    nftContractAddress,
    escrowContractAddres,
    token,
    resellContract,
    identityVerification,
  } = getContractAddresses() as ContractAddresses;

  const currentaddress: string =
    isToken === "token"
      ? token
      : isEscrow
      ? escrowContractAddres
      : isResell
      ? resellContract
      : isVerified
      ? identityVerification
      : nftContractAddress;

  // Use type assertion to treat the ABIs as AbiItem[]
  const currentABI = (
    isToken === "token"
      ? tokenABI
      : isEscrow
      ? escrowABI
      : isResell
      ? resellABI
      : isResellPool
      ? resellPoolABI
      : isVerified
      ? verification
      : nftABI
  ) as AbiItem[];

  try {
    if (web3) {
      const contractInstance = new web3.eth.Contract(
        currentABI,
        currentaddress
      );
      return contractInstance;
    }
  } catch (error) {
    console.error("Error creating property contract instance:", error);
    return undefined;
  }
}

/**
 * Gets the property factory contract instance
 * @returns The property factory contract instance or undefined
 */
export function getPropertyContractInstance(): Contract | undefined {
  const { propertyFactory } = getContractAddresses() as ContractAddresses;
  try {
    if (web3) {
      const contractInstance = new web3.eth.Contract(
        propertyFactoryABI as unknown as AbiItem[],
        propertyFactory
      );
      return contractInstance;
    }
  } catch (error) {
    console.error("Error creating property factory contract instance:", error);
    return undefined;
  }
}

/**
 * Gets the rental pool contract instance
 * @returns The rental pool contract instance or undefined
 */
export function getRentalContractInstance(): Contract | undefined {
  const { rentalPool } = getContractAddresses() as ContractAddresses;
  try {
    if (web3) {
      const contractInstance = new web3.eth.Contract(
        rentalPoolABI as unknown as AbiItem[],
        rentalPool
      );
      return contractInstance;
    }
  } catch (error) {
    console.error("Error creating rental pool contract instance:", error);
    return undefined;
  }
}

/**
 * Gets the resell pool contract instance
 * @returns The resell pool contract instance or undefined
 */
export function getResellPoolContractInstance(): Contract | undefined {
  try {
    const { resellContract } = getContractAddresses() as ContractAddresses;

    if (!web3) {
      return undefined;
    }

    if (!resellContract) {
      return undefined;
    }

    const contractInstance = new web3.eth.Contract(
      resellPoolABI as unknown as AbiItem[],
      resellContract
    );

    return contractInstance;
  } catch (error) {
    console.error("Error in getResellPoolContractInstance:", error);
    if (error instanceof Error) {
      console.error("Error details:", {
        name: error.name,
        message: error.message,
        stack: error.stack,
      });
    }
    return undefined;
  }
}

/**
 * Gets the file type from a MinIO/S3 URL
 * @param url - The MinIO/S3 URL
 * @returns The file type
 */
export async function getFileTypeFromUrl(url: string): Promise<string> {
  const key = url.substring(url.lastIndexOf("/") + 1); // extract file key from URL
  const ext = await getFile(key);
  return ext.substring(0, ext.lastIndexOf("/"));
}

/**
 * Compacts an address for display
 * @param address - The address to compact
 * @returns The compacted address
 */
export function _compactAddress(address: string): string | undefined {
  const newAddress = address;
  if (address) {
    return (
      newAddress.substring(0, 5) +
      "...." +
      newAddress.substring(newAddress.length - 10, newAddress.length)
    );
  }
  return undefined;
}

/**
 * Gets the file metadata from MinIO
 * @param key - The MinIO object key
 * @returns The MIME type of the file
 */
export async function getFile(key: string): Promise<string> {
  try {
    const object = await getFromMinIO(key);
    if (!object.ContentType) {
      throw new Error("Could not determine file type");
    }
    return object.ContentType;
  } catch (error) {
    console.error("Error getting file from MinIO:", error);
    throw new Error("Could not determine file type");
  }
}

/**
 * Gets the file format based on the file type
 * @param fileType - The file type
 * @returns The file format
 */
export function getFileFormat(fileType: string): string {
  if (fileType.includes("image")) return "image";
  else if (fileType.includes("audio")) return "audio";
  else if (fileType.includes("video")) return "video";
  else return "image";
}

/**
 * Creates a property contract instance for a specific contract address
 * @param contractAddress - The contract address
 * @returns The property contract instance or undefined
 */
export function createPropertyContractInstance(
  contractAddress: string
): Contract | undefined {
  try {
    if (web3) {
      const contractInstance = new web3.eth.Contract(
        propertyAsset as unknown as AbiItem[],
        contractAddress
      );
      return contractInstance;
    }
  } catch (error) {
    console.error("Error creating contract instance:", error);
    return undefined;
  }
}

/**
 * Gets the token balance for a wallet address
 * @param tokenContractAddress - The token contract address
 * @param walletAddress - The wallet address
 * @returns The token balance or null if an error occurs
 */
export async function getTokenBalance(
  tokenContractAddress: string,
  walletAddress: string
): Promise<string | null> {
  try {
    if (!web3) {
      console.error("Web3 is not initialized");
      return null;
    }
    if (!tokenContractAddress) {
      console.error("Token contract address is missing");
      return null;
    }
    if (!walletAddress) {
      console.error("Wallet address is missing");
      return null;
    }

    const tokenContract = new web3.eth.Contract(
      tokenABI as unknown as AbiItem[],
      tokenContractAddress
    );
    const rawBalance = await tokenContract.methods
      .balanceOf(walletAddress)
      .call();
    const formattedBalance = web3.utils.fromWei(rawBalance, "ether"); // or adjust decimals if different

    return formattedBalance;
  } catch (error) {
    console.error("Error fetching token balance:", error);
    return null;
  }
}

/**
 * Gets the maximum total supply for a property asset contract
 * @param contractAddress - The property asset contract address
 * @returns The maximum total supply or null if an error occurs
 */
export async function getMaxTotalSupply(
  contractAddress: string
): Promise<string | null> {
  try {
    if (!web3) {
      console.error("Web3 is not initialized");
      return null;
    }

    const propertyContract = createPropertyContractInstance(contractAddress);
    if (!propertyContract) {
      console.error("Failed to create property contract instance");
      return null;
    }

    const maxSupply = await propertyContract.methods.maxTotalSupply().call();
    return web3.utils.fromWei(maxSupply, "ether"); // Convert from wei to ether
  } catch (error) {
    console.error("Error fetching max total supply:", error);
    return null;
  }
}
